import React from "react";
import { DOC_ZOOM_WIDTH_VAR } from "../hooks/useDocumentZoom";
import { printPageMarginCss } from "../utils/printPageGeometry";

/**
 * CSS overrides for workspace document rendering.
 *
 * These styles are scoped to `.workspace-doc-container` / `.workspace-doc-root`
 * and handle:
 * - Disabling the default doc-root scrolling so the outer container scrolls instead.
 * - Neutralising DocumentRenderer's transform: scale() (zoom is via CSS `zoom`).
 * - Pinning the doc-root to the measured container width, published by
 *   `useDocumentZoom` as `--doc-zoom-width`, so CSS `zoom` scales the content
 *   instead of reflowing it.
 * - Print-specific layout: auto height, page breaks per OCR page, no shadows.
 *
 * The `@page` rule deliberately overrides the shared one in `PrintStyles` (this
 * component is mounted after it, and `@page` rules carry no selector, so the
 * later declaration wins — and only on this page, where this component is
 * mounted). Workspace documents print as fixed-aspect page rasters, so the
 * printed content box has to be at least as tall-proportioned as the paper —
 * see `utils/printPageGeometry`. Legislation prints reflowable text and keeps
 * the shared geometry. Nothing mounted later declares `@page`: the converter CSS
 * that `DocumentStyles` scopes into the page has none.
 */
const WorkspaceDocumentStyles: React.FC = () => (
  <style
    dangerouslySetInnerHTML={{
      __html: `
        .workspace-doc-container .doc-root {
          height: auto !important;
          overflow-y: visible !important;
          overflow-x: visible !important;
          transform: none !important;
          margin: 0 auto !important;
          /* Fixed px width (measured by useDocumentZoom) so CSS zoom scales
             responsive content instead of reflowing it. Not an inline style:
             inline !important would outrank the print resets below. */
          width: var(${DOC_ZOOM_WIDTH_VAR}, 100%);
        }
        @media print {
          @page {
            size: A4;
            margin: ${printPageMarginCss()};
          }
          /* Reset zoom and the pinned width for print */
          .workspace-doc-container .doc-root {
            zoom: 1 !important;
            transform: none !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          /* Make layout flow for print (but DON'T reset position on descendants -
             OCR content needs absolute positioning for .t divs overlaying images) */
          .workspace-doc-root {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          .workspace-doc-container {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          /* Each OCR page should start on its own print page */
          .workspace-doc-container .page-container {
            break-inside: avoid;
            page-break-inside: avoid;
            break-after: page;
            page-break-after: always;
            margin: 0 auto !important;
            box-shadow: none !important;
            border-bottom: none !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          /* Don't break after the very last page */
          .workspace-doc-container .page-container:last-child {
            break-after: auto;
            page-break-after: auto;
          }
          /* Off-screen pages skip rendering on screen (content-visibility: auto,
             set inline by the parser — LAR-487). Print needs every page, and an
             inline style only yields to !important. */
          .workspace-doc-container .ocr-anchor-wrapper {
            content-visibility: visible !important;
          }
        }
      `,
    }}
  />
);

export default WorkspaceDocumentStyles;
